import React, { MouseEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import cn from 'classnames';

import { DynamicIconWrapper, Icons, Tooltip } from '@components/common';
import UserSpinner from '@components/common/UserSpinner';

import { useMutatePortalMilestones } from '@hooks/portal/milestones/useMutatePortalMilestones';
import { useMutatePortalTasks } from '@hooks/portal/tasks/useMutatePortalTasks';
import { useNotifications } from '@hooks/useNotifications';
import { useOnClickOutside } from '@hooks/utils/useOnClickOutSide';

import { auth, notifications } from '@store/selectors';
import { useAppSelector } from '@store/store';

import { portalSearchQueries } from '@components/routes/AdminPanel/ProjectProfile/constants';

import NotificationItem from './NotificationItem';

import styles from './Notifications.module.scss';

const MIN_SCROLL_BOTTOM_HEIGHT = 200;

interface INotificationsProps {
	isMapMode: boolean;
	hideMapControls: () => void;
}

const Notifications = ({
	isMapMode,
	hideMapControls,
}: INotificationsProps) => {
	const navigate = useNavigate();

	const { activeOrganisation } = useAppSelector(auth);

	const {
		getNotifications,
		removeNotification,
		readAllNotifications,
		removeAllNotifications,
	} = useNotifications();

	const {
		notificationList,
		unreadNotificationsCount,
		notificationsCount,
		isLoadingNotifications,
	} = useAppSelector(notifications);

	const { handleCheckTaskExists } = useMutatePortalTasks({});
	const { handleCheckMilestoneExists } = useMutatePortalMilestones({});

	useEffect(() => {
		if (activeOrganisation) {
			getNotifications({ offset: 0 } as any);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeOrganisation]);

	const notificationRef = useRef<HTMLDivElement | null>(null);
	const notificationIconRef = useRef<HTMLDivElement | null>(null);
	const contentScrollRef = useRef<HTMLDivElement | null>(null);

	const [isNotificationsMenuOpen, setIsNotificationsMenuOpen] = useState(false);

	const notificationsClasses = cn(styles.notificationsContainer, {
		[styles.showMenu]: isNotificationsMenuOpen,
	});

	const toggleNotifications = () => {
		if (isNotificationsMenuOpen) readAllNotifications();

		setIsNotificationsMenuOpen(prevState => !prevState);
		hideMapControls();
	};

	useOnClickOutside({
		refs: [notificationRef, notificationIconRef],
		handler: () => isNotificationsMenuOpen && toggleNotifications(),
	});

	const onScrollHandler = useCallback((e: React.UIEvent) => {
		const allVisibleHeightWithScroll = e.currentTarget.scrollTop + e.currentTarget.clientHeight;
		const heightForScroll = e.currentTarget.scrollHeight - MIN_SCROLL_BOTTOM_HEIGHT;

		if (!isLoadingNotifications
			&& (notificationList.length < notificationsCount)
			&& (allVisibleHeightWithScroll > heightForScroll)) {
			getNotifications({ offset: notificationList.length } as any);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isLoadingNotifications, notificationList, notificationsCount]);

	const handleRemoveNotification = (id: string) => removeNotification(id as any);

	const onClickClearAll = () => removeAllNotifications();

	useEffect(() => {
		if (isNotificationsMenuOpen && notificationList.length && contentScrollRef.current) {
			contentScrollRef.current.scrollTop = 0;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isNotificationsMenuOpen]);

	const handleLinkClick = async (event: MouseEvent<HTMLDivElement>) => {
		const target = event.target as HTMLAnchorElement;

		if (!target.href) return;

		const url = new URL(target.href);
		const taskId = url.searchParams.get(portalSearchQueries.task);
		const milestoneId = url.searchParams.get(portalSearchQueries.milestone);

		if (!(taskId || milestoneId)) return;

		if (target.tagName === 'A') {
			event.preventDefault();

			try {
				if (taskId) {
					await handleCheckTaskExists({ taskId });
				} else if (milestoneId) {
					await handleCheckMilestoneExists({ milestoneId });
				}

				navigate(url.pathname + url.search);
			} catch (err) {
				return err;
			} finally {
				toggleNotifications();
			}
		}
	};

	return (
		<>
			<Tooltip
				content='Notifications'
				noWordBreak
			>
				<div
					ref={notificationIconRef}
					className={cn(styles.notificationsIcon, { [styles.mapIcon]: isMapMode })}
					onClick={toggleNotifications}
				>
					<DynamicIconWrapper
						icon={Icons.notification}
						isActive={isNotificationsMenuOpen}
						isUnreadNotificationsCount={unreadNotificationsCount > 0}
					/>
				</div>
			</Tooltip>
			<div
				ref={notificationRef}
				className={notificationsClasses}
			>
				<div className={styles.header}>
					<div>Notifications</div>
					<div
						className={styles.clear}
						onClick={onClickClearAll}
					>
						Clear all
					</div>
				</div>
				<div
					ref={contentScrollRef}
					className={styles.content}
					onScroll={onScrollHandler}
				>
					{notificationList.length
						? (
							notificationList.map(({
								createdAt,
								id,
								isRead,
								text,
								title,
							}, index) => {

								return (
									<NotificationItem
										key={id + index || createdAt + index}
										createdAt={createdAt}
										handleLinkClick={handleLinkClick}
										handleRemoveNotification={handleRemoveNotification}
										id={id}
										isRead={isRead}
										text={text}
										title={title}
									/>
								);

							})
						)
						: (
							<div className={styles.emptyMessage}>
								You don’t have any notifications right now.
							</div>
						)
					}
					{isLoadingNotifications
						&& <div className={styles.spinnerContainer}>
							<UserSpinner size={30} />
						</div>
					}
				</div>
			</div>
		</>
	);
};

export default Notifications;
